import { IconButton, theme } from '@acorns/grove'
import { AcornsIcon } from '@acorns/icons'
import { useAnalyticsClient } from '@acorns/web-utils'
import React, { KeyboardEvent, MouseEvent, useContext, useEffect, useState } from 'react'

import { TestIDs } from 'test/utils/testIds'
import { RegistrationProminenceBannerContent } from 'generated/types'
import { updateThemeColor } from 'theme'

import { Drawer } from 'components/templates/Drawer'
import { CodeRenderer, LinkRenderer } from 'components/templates/shared-components'
import {
  trackRegistrationContextualFunnelBannerBannerDismissedPayload,
  trackRegistrationContextualFunnelBannerBannerViewedPayload,
  trackRegistrationContextualFunnelBannerTermsTappedPayload,
} from 'generated/trackingEvents'
import { translateEventProps } from 'utils/analytics'

import { FunnelContentContext } from '../FunnelContentProvider'
import {
  Container,
  DismissContainer,
  IconContainer,
  BannerIcon,
  BannerTitle,
} from './styled-components'

const ANCHOR_TAG = 'a'

interface Props {
  content: RegistrationProminenceBannerContent
  eventObjectName: string
}

export const ProminenceBanner = ({ content, eventObjectName }: Props) => {
  const [className, setClassName] = useState('')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const analytics = useAnalyticsClient()
  const {
    showProminenceBanner,
    setShowProminenceBanner,
    prominenceBannerAnimated,
    setProminenceBannerAnimated,
  } = useContext(FunnelContentContext)
  const eventData = {
    screen: eventObjectName,
    campaignId: content.campaignId,
  }

  useEffect(() => {
    const [eventName, payload] = trackRegistrationContextualFunnelBannerBannerViewedPayload(
      eventData,
    )
    analytics.track(eventName, translateEventProps(payload))
  }, [analytics])

  const handleTitleLink = (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).tagName.toLowerCase() === ANCHOR_TAG) {
      const [eventName, payload] = trackRegistrationContextualFunnelBannerTermsTappedPayload(
        eventData,
      )
      analytics.track(eventName, translateEventProps(payload))
      setIsDrawerOpen(true)
    }
  }

  const handleDismiss = () => {
    setClassName('')
    setTimeout(updateThemeColor, 500)
    const [eventName, payload] = trackRegistrationContextualFunnelBannerBannerDismissedPayload(
      eventData,
    )
    analytics.track(eventName, translateEventProps(payload))
    setShowProminenceBanner(false)
  }

  useEffect(() => {
    setTimeout(() => {
      if (!prominenceBannerAnimated) {
        setClassName('animated')

        setTimeout(() => {
          setProminenceBannerAnimated(true)
        }, 600)
      }

      updateThemeColor(theme.colors.naturalCloud)
    }, 500)

    return updateThemeColor
  }, [setProminenceBannerAnimated, prominenceBannerAnimated, setShowProminenceBanner])

  if (!showProminenceBanner) {
    return null
  }

  return (
    <>
      <Container
        animate={!prominenceBannerAnimated}
        className={className}
        data-testid={TestIDs.PROMINENCE_BANNER.CONTAINER}
      >
        <IconContainer>
          <BannerIcon
            data-testid={TestIDs.PROMINENCE_BANNER.ICON}
            src={content.icon}
            alt="Prominence Banner Icon"
          />
        </IconContainer>
        {/* The onKeyDown handler was added primarily to satisfy the linter, for most users, this will have no effect
        for users with a screenreader, this should provide the ability to open the drawer */}
        <div
          data-testid={TestIDs.PROMINENCE_BANNER.TITLE}
          onClick={handleTitleLink}
          onKeyDown={handleTitleLink}
        >
          <BannerTitle
            source={content.title}
            renderers={{ Link: LinkRenderer, Code: CodeRenderer }}
          />
        </div>
        <DismissContainer>
          <IconButton
            onClick={handleDismiss}
            testId={TestIDs.PROMINENCE_BANNER.DISMISS_BUTTON}
            isLight={true}
            isTransparent={true}
            icon={<AcornsIcon height={12} width={12} icon={AcornsIcon.Icon.InlineClose} />}
          />
        </DismissContainer>
      </Container>
      {content.drawer && (
        <Drawer
          {...content.drawer}
          eventData={eventData}
          isOpen={isDrawerOpen}
          setIsOpen={setIsDrawerOpen}
        />
      )}
    </>
  )
}
